import {
  AfterViewInit,
  Component,
  OnInit,
  ViewChild,
  ViewContainerRef,
  EnvironmentInjector,
  inject,
  effect,
  Input,
  SimpleChanges,
  OnChanges,
  HostListener,
} from '@angular/core';
import {
  GridStack,
  GridStackElement,
  GridStackNode,
  GridStackOptions,
  GridStackWidget,
} from 'gridstack';

import { Button } from 'primeng/button';
import { DividerModule } from 'primeng/divider';
import { WidgetAdderComponent } from '../widget-adder/widget-adder.component';
import { PrimeTemplate } from 'primeng/api';
import { SpeedDialModule } from 'primeng/speeddial';
import { CustomWidgetComponent } from '../custom-widget/custom-widget.component';
import { GridStore } from '../../../stores/grid/grid.state';
import {
  CustomGridStackWidget,
  GridWidgetConfigService,
  WidgetConfig,
  WidgetType,
} from '../../../services/grid-widget-config.service';
import { WidgetStore } from '../../../stores/grid/widget.state';
import { SidebarModule } from 'primeng/sidebar';
import { StepperModule } from 'primeng/stepper';
import {
  AtlasEventKey,
  AtlasEventOptions,
  GlobalsStore,
} from '../../../stores/globals.state';
import { AccordionModule } from 'primeng/accordion';
import { WidgetIndicatorsComponent } from '../../molecules/widget-indicators/widget-indicators.component';
import { WidgetDataLayerComponent } from '../../molecules/widget-data-layer/widget-data-layer.component';
import { getState } from '@ngrx/signals';
import { WidgetConfigFormComponent } from '../../molecules/widget-config-form/widget-config-form.component';
import { ConfigFormOptions } from '../../../models/widget';
import { EntityId } from '@ngrx/signals/entities';
import { MaskComponent } from '../mask/mask.component';
import { deepMerge } from '../../../utility/array.utility';
import { WidgetHelperService } from '../../../services/widget/widget-helper.service';
import { AtlasTemplateService } from '../../../services/atlas-template.service';
import { Bookmark, LayoutOptions } from '../../../models/bookmark';
import { WidgetExportComponent } from '../widget-export/widget-export.component';
import { RoutingService } from '../../../services/routing.service';
import { DistrictReportDropdownComponent } from '../../atoms/district-report-dropdown/district-report-dropdown.component';
import { AtlasEditButtonComponent } from '../../atoms/atlas-edit-button/atlas-edit-button.component';
import { TreeModule } from 'primeng/tree';
import { WegweiserStore } from '../../../stores/wegweiser-api/wegweiser.state';
import { HeaderComponent } from '../../atoms/header/header.component';
import { BookmarkStore } from '../../../stores/bookmark/bookmark.state';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { NgClass, NgIf } from '@angular/common';
import { ActiveUserStore } from '../../../stores/user/active-user.state';
import { Commune } from '../../../models/commune';
import { PoiCheckboxesComponent } from '../poi-checkboxes/poi-checkboxes.component';

@Component({
  selector: 'app-atlas-grid',
  standalone: true,
  imports: [
    DividerModule,
    WidgetAdderComponent,
    Button,
    PrimeTemplate,
    SpeedDialModule,
    SidebarModule,
    StepperModule,
    AccordionModule,
    WidgetIndicatorsComponent,
    WidgetDataLayerComponent,
    WidgetConfigFormComponent,
    MaskComponent,
    WidgetExportComponent,
    DistrictReportDropdownComponent,
    AtlasEditButtonComponent,
    TreeModule,
    HeaderComponent,
    ConfirmDialogModule,
    NgIf,
    NgClass,
    PoiCheckboxesComponent,
  ],
  templateUrl: './atlas-grid.component.html',
  styleUrl: './atlas-grid.component.scss',
})
export class AtlasGridComponent implements OnInit, AfterViewInit, OnChanges {
  readonly bookmarkStore = inject(BookmarkStore);
  readonly globalStore = inject(GlobalsStore);
  readonly gridStore = inject(GridStore);
  readonly widgetStore = inject(WidgetStore);
  readonly activeUserStore = inject(ActiveUserStore);
  readonly wegweiserStore = inject(WegweiserStore);
  @ViewChild('dynamicContainer', { read: ViewContainerRef })
  viewContainerRef!: ViewContainerRef;
  @ViewChild('widgetAdder') private widgetAdder?: WidgetAdderComponent;

  @Input() bookmark?: Bookmark;
  @Input() gridId!: string;
  @Input() forceReadOnly = false;

  /**
   * Handles change detection for input properties in the component.
   *
   * This lifecycle hook is called whenever Angular sets an input property
   * of the component to a new value. It performs operations based on the
   * changes detected in the inputs.
   *
   * Is normally used for story-gallery bookmarks
   *
   * @param {SimpleChanges} changes - Object that represents the new and previous
   * values of the input-bound properties that have changed. Each property in this
   * object is an instance of `SimpleChange`.
   * @return {void}
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['bookmark'] && changes['bookmark'].currentValue) {
      const bookmark = changes['bookmark'].currentValue;
      this.loadLayout(structuredClone(bookmark.config), bookmark.layoutOptions);
    }
  }

  protected type: WidgetType = 'adder';
  private grid!: GridStack;
  options!: GridStackOptions;
  activeWidget: CustomGridStackWidget | null = null;
  activeBookmarkId = '';
  currentWidgetConfig: WidgetConfig | null = null;
  activeConfig: number | boolean = false;
  selectedWidgetId: EntityId | null = null;
  minIndicators = 1;
  maxIndicators = 999;
  initialized = false;
  readonly = this.forceReadOnly || false;
  quickSwitchPoi = false;

  // todo: set final breakpoints
  breakpoints = [
    { w: 640, c: 1 },
    { w: 850, c: 3 },
    { w: 950, c: 6 },
    { w: 1100, c: 8 },
  ];

  public items: CustomGridStackWidget[] = [];

  constructor(
    private environmentInjector: EnvironmentInjector,
    private templateService: AtlasTemplateService,
    protected configService: GridWidgetConfigService,
    protected widgetHelper: WidgetHelperService,
    private routingService: RoutingService,
    private confirmationService: ConfirmationDialogService
  ) {
    effect(() => {
      const widgetState = getState(this.widgetStore);
      const gridState = getState(this.gridStore);
      const globalState = getState(this.globalStore);
      const bookmarkState = getState(this.bookmarkStore);

      this.activeConfig = widgetState.activeConfig;
      this.activeWidget = this.widgetStore.selected();
      this.currentWidgetConfig = this.widgetStore.currentWidgetConfig();
      this.selectedWidgetId = this.widgetStore.selected()?.id ?? null;

      if (this.forceReadOnly) return;
      if (
        bookmarkState.trigger &&
        (bookmarkState.trigger.includes('bookmarkAdded') ||
          bookmarkState.trigger.includes('bookmarkUpdated'))
      ) {
        setTimeout(() => {
          this.activeBookmarkId = bookmarkState.lastPersistedId;
          this.routingService
            .openBookmarkRoute(this.activeBookmarkId)
            .then(() => {
              this.gridStore.setActiveBookmarkById(this.activeBookmarkId);
              this.gridStore.setReadonly(false);
              this.readonly = false;
            });
        }, 300);
      }

      if (
        (gridState.activeBookmark && gridState.readonly === null) ||
        this.forceReadOnly
      ) {
        queueMicrotask(() => {
          this.gridStore.setReadonly(true);
          this.readonly = true;
          this.setupGridEvents();
        });
      }

      if (!gridState.activeBookmark && this.activeBookmarkId) {
        queueMicrotask(() => {
          this.clear();
          this.activeBookmarkId = '';
        });
      }

      if (
        this.initialized &&
        this.readonly &&
        gridState.readonly === false &&
        !this.forceReadOnly
      ) {
        queueMicrotask(() => {
          this.readonly = false;
          this.setupGridEvents();
        });
      }

      if (globalState.atlasEventCallback) {
        this.callAtlasEvent(
          globalState.atlasEventCallback.key,
          globalState.atlasEventCallback.options
        );
      }

      if (
        gridState.overwriteLayout &&
        gridState.activeBookmark?._id !== this.activeBookmarkId
      ) {
        queueMicrotask(() => {
          this.loadLayout(this.gridStore.layout() || []);
          this.activeBookmarkId = gridState.activeBookmark?._id || '';
        });
      }

      // save layout when grid-store triggers a change
      if (gridState.layoutChanged && gridState.initialized) {
        this.saveGridLayout();
      }
      if (!this.activeWidget) return;

      const selectionMode = this.configService.getIndicatorModeByType(
        this.activeWidget.type
      );

      this.minIndicators =
        selectionMode === 'double' ? 2 : selectionMode === 'triple' ? 3 : 1;
      this.maxIndicators =
        selectionMode === 'double' ? 2 : selectionMode === 'triple' ? 3 : 999;
    });
    this.initialized = true;
  }

  @HostListener('window:beforeunload', ['$event'])
  unloadNotification($event: any) {
    if (this.gridStore.gridHasChanges()) {
      $event.returnValue = true;
    }
  }

  /**
   * Lifecycle hook called after Angular has initialized all data-bound properties of a component.
   * This method initializes the grid system options and fetches topics from the wegweiserStore.
   *
   * @return {void} Does not return a value.
   */
  public ngOnInit(): void {
    this.options = {
      column: 12,
      handle: '.gs-handler',
      cellHeight: '12rem',
      minRow: 2,
      margin: 12,
      float: true,
      acceptWidgets: true,
      resizable: { autoHide: true, handles: 'e,se,s,sw,w' },
      removable: false,
      columnOpts: {
        breakpointForWindow: true, // test window vs grid size
        breakpoints: this.breakpoints,
      },
    };
    this.wegweiserStore.fetchTopics().then(() => {
      //console.log('ALL TOPICS', this.wegweiserStore.topics());
    });
  }

  /**
   * initialize grid and grid-handler
   * adds initial adder-widget
   */
  public ngAfterViewInit(): void {
    this.init();
  }

  /**
   * Initializes the grid layout. If a bookmark is present, it loads the layout configuration.
   * Otherwise, it initializes the default grid configuration, drag-and-drop functionality,
   * grid events, and an additional widget. Marks the grid as initialized after a delay.
   *
   * @return {void} Does not return any value.
   */
  init(): void {
    if (this.forceReadOnly) {
      this.gridStore.setReadonly(true);
    }
    if (this.bookmark) {
      if (!this.grid) this.setupGridEvents();
      this.loadLayout(
        this.bookmark.config ? structuredClone(this.bookmark.config) : []
      );

      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 500);
    } else {
      this.setupGridEvents();
      this.addAdderWidget();

      setTimeout(() => {
        this.gridStore.setInitialized(true);
      }, 500);
    }
  }

  /**
   * Sets up the grid events by initializing the grid, enabling drag and drop functionality,
   * and adding event handlers for grid interactions.
   *
   * @return {void} Does not return a value.
   */
  setupGridEvents(): void {
    this.initializeGrid();
    this.initializeDragAndDrop();
    this.addGridEvents();
  }

  /**
   * Initializes the grid system by creating a GridStack instance
   * with the specified options and binding it to the element
   * identified by the gridId.
   *
   * @return {void} No return value.
   */
  initializeGrid(): void {
    this.grid = GridStack.init(this.options, `#${this.gridId}`);
  }

  /**
   * Attaches event listeners to the grid for handling various grid-related actions such as adding, dropping, removing, and resizing items.
   * Updates the grid and associated data stores accordingly.
   *
   * @return {void} No return value.
   */
  addGridEvents(): void {
    this.grid.on('added', (event, items) => {
      this.handleAddEvent(event, items);
    });
    this.grid.on('dropped', (event, item, newNode) => {
      this.handleDropEvent(event, item, newNode);
    });
    this.grid.on('removed', (event, items) => {
      this.handleRemoveEvent(event, items);
    });
    // Listen to the 'change' event (e.g., after drag or resize)
    this.grid.on('change', (event: Event, items: any[]) => {
      items.forEach(item => {
        const config = {
          ...item,
          ...this.widgetStore.get(item.id),
          ...{ w: item.w, h: item.h, x: item.x, y: item.y },
        };
        this.widgetStore.update(item.id, config);
        this.grid.update(item, config);
      });
      this.gridStore.triggerLayoutChanges();
    });
  }

  /**
   * Adds an "adder" widget to the widget store if it does not already exist.
   * The addition is executed after a delay of 100 milliseconds.
   *
   * @return {void} This method does not return a value.
   */
  addAdderWidget(): void {
    setTimeout(() => {
      if (!this.gridStore.readonly() && !this.widgetStore.hasAdderWidget()) {
        this.add('adder');
      }
    }, 100);
  }

  /**
   * Handles the drag event by creating and configuring a new HTML element
   * based on the provided event element's attributes and appending an icon to it.
   *
   * @param {HTMLElement} event - The source HTML element for the drag event,
   * which may have attributes necessary for creating the dragged element.
   * @return {HTMLElement} Returns the newly created HTML element with configured attributes and appended icon.
   */
  private handleDragEvent(event: HTMLElement): HTMLElement {
    const dragElement = document.createElement('div');
    dragElement.setAttribute(
      'data-type',
      event.getAttribute('data-type') || 'adder'
    );

    dragElement.className =
      'text-5xl flex align-items-center surface-ground border-round-2xl text-primary w-4 h-24rem';
    const icon = document.createElement('i');
    icon.className = 'fas fa-circle-arrow-down block mx-auto px-3 py-2';
    dragElement.appendChild(icon);

    return dragElement;
  }

  /**
   * Handles the drop event for a grid stack node when dragged and dropped.
   *
   * @param {any} event - The event object associated with the drop action.
   * @param {GridStackNode} item - The original grid stack node being dropped.
   * @param {GridStackNode} newNode - The newly created node for the dropped item.
   * @return {void} - Does not return a value.
   */
  private handleDropEvent(
    event: any,
    item: GridStackNode,
    newNode: GridStackNode
  ): void {
    const typeFromDraggedAdder = newNode.el?.dataset['type'] as WidgetType;
    if (typeFromDraggedAdder && newNode.el) {
      this.grid.removeWidget(newNode.el);
      const widget = this.generateWidget(typeFromDraggedAdder) as GridStackNode;
      if (!widget.el) return;
      this.grid.addWidget(widget);
    }
  }

  /**
   * handles grid-add-event
   * @param event
   * @param items
   * @private
   */
  private handleAddEvent(event: any, items: any[]): void {
    this.widgetAdder?.closeAdder();
    this.gridStore.triggerLayoutChanges();
  }

  /**
   * Handles the remove event by removing specified items from the widget store and triggering layout changes in the grid store.
   *
   * @param {any} event - Event object that triggers the removal logic.
   * @param {any} items - List of items to be removed, each containing an `id` property.
   * @return {void} This method does not return any value.
   */
  private handleRemoveEvent(event: any, items: any): void {
    items.forEach((item: any) => {
      this.widgetStore.remove(item.id);
    });
    this.gridStore.triggerLayoutChanges();
  }

  /**
   * Saves the current grid layout by collecting the configuration of all widgets
   * except the 'adder' type. Each widget's configuration is enriched with custom
   * properties and transformed into a savable format before storing the layout.
   *
   * @return {void} Doesn't return a value.
   */
  public saveGridLayout(): void {
    const widgets = this.widgetStore.asArray();
    // Capture each widget's configuration with custom properties
    const enrichedLayout = widgets
      .filter(item => item.type !== 'adder')
      .map((item: CustomGridStackWidget) => {
        return this.configService.transformWidgetToSavableItem(item);
      });

    setTimeout(() => {
      this.gridStore.saveLayout(enrichedLayout);
    }, 100);
  }

  /**
   * Generates and initializes a new widget based on the specified type and layout configuration.
   *
   * @param {WidgetType} type - The type of widget to generate.
   * @param {any} [layout] - Optional layout configuration for the widget.
   * @return {CustomGridStackWidget} The newly created and configured widget instance.
   */
  generateWidget(type: WidgetType, layout?: any): CustomGridStackWidget {
    this.type = type;
    const newWidget =
      layout && layout.widget
        ? this.widgetStore.controlConfig(
            layout,
            this.widgetHelper.isDataWidget(this.type) || this.type === 'info'
          )
        : this.widgetStore.initializeConfig(type);

    const gridStackElement = this.injectGridWidgetComponent(
      newWidget,
      type,
      !!layout
    );

    this.widgetStore.add(newWidget);
    newWidget.el = gridStackElement;

    return newWidget;
  }

  /**
   * Adds a widget of the specified type to the grid.
   *
   * @param {WidgetType} type - The type of the widget to be added.
   * @return {void} This method does not return a value.
   */
  public add(type: WidgetType): void {
    this.grid.addWidget(this.generateWidget(type) as GridStackWidget);
  }

  /**
   * Initializes the drag and drop functionality for specific draggable elements.
   *
   * @return {void} This method does not return a value.
   */
  public initializeDragAndDrop(): void {
    setTimeout(() => {
      GridStack.setupDragIn('.widget-adder--draggable', {
        appendTo: 'body',
        helper: (event: HTMLElement) => this.handleDragEvent(event),
      });
    });
  }

  /**
   * Deletes the first widget from the grid.
   *
   * The method removes the first widget element using the underlying grid engine.
   * It does not return any value.
   *
   * @return {void} This method does not return any value.
   */
  public delete(): void {
    this.grid.removeWidget(this.grid.engine.nodes[0].el!);
  }

  /**
   * Injects a grid widget component into the DOM and initializes it with the provided options.
   *
   * @param {any} options - Configuration options for the widget to be injected, including its unique identifier.
   * @param {WidgetType} [type] - Specifies the type of the widget to be created. This parameter is optional.
   * @param {boolean} [injectByBookmark=false] - Indicates whether the widget is being injected via a bookmark. Defaults to false.
   * @return {GridStackElement} The created grid stack element representing the widget.
   */
  private injectGridWidgetComponent(
    options: any,
    type?: WidgetType,
    injectByBookmark = false
  ): GridStackElement {
    const wrapperDiv = document.createElement('div');
    wrapperDiv.classList.add('grid-stack-item-content');

    const componentRef = this.viewContainerRef.createComponent(
      CustomWidgetComponent,
      {
        environmentInjector: this.environmentInjector,
      }
    );

    componentRef.setInput('id', options._id);
    if (type !== 'adder') {
      componentRef.setInput('fromBookmark', injectByBookmark);
      componentRef.setInput('initialType', type);
    }

    // Subscribe to the output events
    componentRef.instance.removeWidget.subscribe((id: string) => {
      this.onWidgetRemove(id);
    });
    componentRef.instance.configWidget.subscribe((id: string) => {
      this.onWidgetConfig(id);
    });

    const widget = document.createElement('div');
    widget.classList.add('grid-stack-item', 'flex', 'flex-column');
    widget.appendChild(wrapperDiv);
    wrapperDiv.appendChild(componentRef.location.nativeElement);
    widget.appendChild(wrapperDiv);
    return widget;
  }

  /**
   * Handles the removal of a widget from the grid.
   *
   * @param {string} id - The unique identifier of the widget to be removed.
   * @return {void} This method does not return a value.
   */
  onWidgetRemove(id: string): void {
    const gridNode = this.widgetStore.get(id)?.el;
    if (gridNode) this.grid.removeWidget(gridNode);
  }

  /**
   * handles on-config-event
   * @param id
   */
  onWidgetConfig(id: string) {
    //console.log('WIDGET CONFIG', this.widgetStore.get(id));
  }

  /**
   * default-form-options
   */
  formOptions: ConfigFormOptions = {
    showColorset: true,
    showDisabledFeatures: true,
    showDisabledYears: true,
    showFocusProperty: true,
    showMode: true,
    showDisplayValues: true,
  };

  /**
   * Finalizes the configuration process for the current widget.
   * Ensures that the configuration is properly closed and initializes the widget configuration if not already initialized.
   * Responsible for adding an adder widget and updating the configuration for the selected widget.
   *
   * @return {void} Does not return a value.
   */
  finalizeConfig(): void {
    this.widgetStore.closeConfig();
    this.quickSwitchPoi = false;
    if (this.currentWidgetConfig?.initialized) return;
    setTimeout(() => {
      this.addAdderWidget();
      if (this.selectedWidgetId && this.currentWidgetConfig) {
        this.currentWidgetConfig.initialized = true;
        this.widgetStore.updateWidgetConfigById(
          this.selectedWidgetId,
          this.currentWidgetConfig,
          true
        );
      }
    }, 100);
  }

  /**
   * Cancels the current widget configuration process.
   * Reverts the widget to its previous configuration if applicable
   * and resets the initialized state of the current widget configuration.
   *
   * @return {void} Does not return a value.
   */
  cancel(): void {
    if (!this.selectedWidgetId) return;
    const prevConfig = this.widgetStore.prevConfig();
    if (this.currentWidgetConfig && prevConfig) {
      this.currentWidgetConfig.initialized = false;
      const id = this.selectedWidgetId;
      queueMicrotask(() => {
        this.widgetStore.update(id, prevConfig, true);
      });
    }

    this.widgetStore.closeConfig();
  }

  /**
   * Handles changes to the widget configuration by merging the updated configuration with the current configuration,
   * and updates the widget's state and data accordingly.
   *
   * @param {Partial<WidgetConfig>} conf - The updated widget configuration which will be partially merged with the current configuration.
   * @return {void} Does not return a value.
   */
  onWidgetConfigChange(conf: Partial<WidgetConfig>): void {
    const newConf = deepMerge(this.currentWidgetConfig, conf);

    if (newConf) {
      this.widgetStore.setWidgetProperties(newConf);

      this.widgetStore.updateChartData();
      this.saveGridLayout();
    }
  }

  /**
   * Adds the specified indicator to the layout by updating the widgets in the widget store.
   * This method ensures the indicator is added to each widget that supports data widgets,
   * updates the widget properties, updates districts and chart data, and triggers a bulk update and layout save.
   *
   * @param {string} indicatorId - The ID of the indicator to be added to the layout.
   * @return {void} - No return value.
   */
  addIndicatorToLayout(indicatorId: string): void {
    const widgets = this.widgetStore.entities();
    widgets.forEach(widget => {
      if (
        this.widgetHelper.isDataWidget(widget.type) &&
        !widget.widget.indicators.includes(indicatorId)
      ) {
        const indicators = [...widget.widget.indicators, ...[indicatorId]];

        this.widgetStore.setWidgetProperties(
          {
            indicators: indicators,
            indicator: indicators[0],
          },
          false,
          widget._id
        );

        this.widgetStore.updateDistricts(widget.id, widget.widget?.dataLayer);
        this.widgetStore.updateChartData(widget.id, false);
      }
    });
    setTimeout(() => {
      this.widgetStore.bulkUpdate(widgets);
    }, 300);
    this.saveGridLayout();
  }

  /**
   * Adds the specified indicator to the layout by updating the widgets in the widget store.
   * This method ensures the indicator is added to each widget that supports data widgets,
   * updates the widget properties, updates districts and chart data, and triggers a bulk update and layout save.
   *
   * @param {string} poiId - The ID of the indicator to be added to the layout.
   * @return {void} - No return value.
   */
  addPoiToMaps(poiId: string): void {
    const widgets = this.widgetStore.entities();
    widgets.forEach(widget => {
      if (!widget.widget || !widget.widget.map) return;
      if (widget.type === 'map' && !widget.widget.map?.poi?.[poiId]) {
        if (!widget.widget.map.poi) {
          widget.widget.map.poi = {};
        }

        widget.widget.map.poi[poiId] = null;
        this.widgetStore.update(widget._id, widget);

        this.widgetStore.updateDistricts(widget.id, widget.widget?.dataLayer);
        this.widgetStore.updateChartData(widget.id, false);
      }
    });
    setTimeout(() => {
      this.widgetStore.bulkUpdate(widgets);
    }, 300);
    this.saveGridLayout();
  }

  /**
   * Loads and adds a given layout to the grid system by processing each widget
   * and applying specific operations based on its type and context.
   *
   * @param {CustomGridStackWidget[]} layout - An array of widgets that define the layout to be loaded.
   * @return {void} This method does not return a value.
   */
  loadLayout(layout: CustomGridStackWidget[], options?: LayoutOptions): void {
    if (!this.grid) return;

    this.grid.removeAll(true);
    try {
      layout.forEach((item: CustomGridStackWidget) => {
        this.loadWidgetFromLayout(item);
      });
      this.saveGridLayout();
    } catch (e) {
      console.log(e);
    }
  }

  /**
   * Loads a widget from the given layout configuration, initializes it, and adds it to the grid.
   * This method ensures the widget context is set appropriately and performs additional updates
   * or restorations based on the widget type and its data.
   *
   * @param {CustomGridStackWidget} item - The layout configuration for the widget to be loaded.
   * @return {void} No value is returned by this method.
   */
  private loadWidgetFromLayout(item: CustomGridStackWidget): void {
    // Deep clone to prevent mutation of original bookmark config
    const clonedItem = structuredClone(item);
    const widget = this.generateWidget(
      clonedItem.type,
      clonedItem as GridStackWidget
    );
    widget.widget.context = widget.widget.context || 'local'; // fallback
    this.grid.addWidget(widget as GridStackWidget);

    if (
      this.widgetHelper.isDataWidget(widget.type) &&
      widget.widget.context !== 'external'
    ) {
      this.widgetStore.updateDistricts(widget.id, widget.widget.dataLayer);
    } else if (widget.widget.wegweiserData?.regions?.length) {
      this.wegweiserStore.setUpWegweiserWidgetByLayout(widget);
      this.wegweiserStore.restoreByWidget(widget.widget.wegweiserData);
    }
    this.widgetStore.updateChartData(widget.id);
  }

  /**
   * Handles various Atlas events based on the provided key and options.
   *
   * @param {AtlasEventKey} key - The key identifying the type of Atlas event to invoke. Possible values include:
   *                              'generateView', 'pasteWidgetCopy', 'addToWidgets', 'loadBookmark',
   *                              'loadStory', 'loadTopic', 'openLandingPage', 'closeLandingPage'.
   * @param {AtlasEventOptions} options - An object containing additional parameters required to execute the specified event.
   *                                      Properties may include `indicatorIds`, `dataLayerTypeId`, `widgetCopy`,
   *                                      `bookmarkId`, `storyId`, `topic`, and other relevant data for the event.
   * @return {void} This method does not return any value. It performs actions such as modifying the UI, updating stores,
   *                navigating routes, and triggering other events based on the input parameters.
   */
  private callAtlasEvent(key: AtlasEventKey, options: AtlasEventOptions): void {
    switch (key) {
      case 'generateView':
        if (options.indicatorIds) {
          setTimeout(() => {
            this.globalStore.triggerAtlasEvent('closeLandingPage', {});
          });
          this.loadLayoutByTemplate(
            options.indicatorIds,
            options.dataLayerTypeId || '',
            options.template
          );
        }
        break;
      case 'pasteWidgetCopy':
        setTimeout(() => {
          if (options.widgetCopy) {
            const widgetToCopy = {
              id: options.widgetCopy.id,
              _id: options.widgetCopy._id,
              h: options.widgetCopy.h,
              w: options.widgetCopy.w,
              widget: options.widgetCopy.widget,
              type: options.widgetCopy.type,
            };

            const widget = this.generateWidget(
              widgetToCopy.type,
              widgetToCopy as GridStackWidget
            );

            this.grid.addWidget(widget as GridStackWidget);
          }
        });
        break;
      case 'addToWidgets':
        setTimeout(() => {
          const indicatorId = options.indicatorIds?.at(0);
          if (indicatorId) {
            this.addIndicatorToLayout(indicatorId);
          }
        });
        break;
      case 'addPoiToMaps':
        setTimeout(() => {
          const poiId = options.poiLayerTypeId;
          if (poiId) {
            this.addPoiToMaps(poiId);
          }
        });
        break;
      case 'loadBookmark':
        setTimeout(() => {
          const bookmarkId = options.bookmarkId;
          if (bookmarkId) {
            this.routingService.openBookmarkRoute(bookmarkId);
          }
        });
        break;
      case 'loadStory':
        setTimeout(() => {
          const storyId = options.storyId;
          if (storyId) {
            this.globalStore.setActiveStory(storyId);
          }
        });
        break;
      case 'loadTopic':
        setTimeout(() => {
          const topic = options.topic;
          if (!topic) return;

          this.globalStore.setActiveTopic(topic);

          const bookmarkToLoad = this.getFirstValidBookmark(topic);

          if (bookmarkToLoad) {
            this.redirectToBookmark(bookmarkToLoad);
          } else {
            this.callStartPageByContext();
          }
        });
        break;
      case 'markDistrict':
        setTimeout(() => {
          this.gridStore.markFeature(String(options.key) || '');
          const widgets = this.widgetStore.entities();
          widgets.forEach((widget: CustomGridStackWidget) => {
            this.widgetStore.updateChartData(widget.id);
          });
          this.widgetStore.bulkUpdate(widgets);
        }, 100);
        break;
      case 'highlightDistrict':
        setTimeout(() => {
          this.gridStore.highlightFeature(
            options.key || undefined,
            options.type || undefined
          );
          const widgets = this.widgetStore.entities();
          for (const item of widgets) {
            this.widgetStore.updateChartData(item.id, false);
          }
          this.widgetStore.bulkUpdate(widgets);
        }, 100);
        break;
      case 'openLandingPage':
        setTimeout(() => {
          this.grid.removeAll();
          this.widgetStore.reset();
          this.globalStore.closeAll();
          this.gridStore.reset();
          this.activeBookmarkId = '';
        });
        break;
      case 'closeLandingPage':
        setTimeout(() => {
          this.init();
        });
        break;
      case 'callStartPage':
        this.callStartPageByContext();
        break;
    }
  }

  /**
   * Retrieves the first valid bookmark from the given topic.
   *
   * @param {any} topic - The topic object containing bookmark information.
   * @return {string | undefined} - Returns the ID of the first valid bookmark if found, otherwise undefined.
   */
  private getFirstValidBookmark(topic: any): string | undefined {
    const bookmarkToLoad = topic.defaultBookmark;

    if (bookmarkToLoad && this.bookmarkStore.has(bookmarkToLoad)) {
      return bookmarkToLoad;
    }

    const firstCategory = topic.bookmarkCategories?.at(0);

    return firstCategory
      ? this.bookmarkStore.groupedByCategory()[firstCategory]?.at(0)?._id
      : undefined;
  }

  /**
   * Redirects the user to a specific bookmark within the current context and commune.
   * If a bookmark ID is not provided, it retrieves the bookmark ID based on the user's
   * current context and active commune.
   *
   * @param {string} [bookmarkId] Optional ID of the bookmark to redirect to. If not specified, the bookmark ID will be determined automatically.
   * @return {void} Does not return a value.
   */
  private redirectToBookmark(bookmarkId?: string): void {
    const commune = this.globalStore.activeCommune();
    if (!commune) return;

    const context = this.activeUserStore.context();
    if (!bookmarkId) {
      bookmarkId = this.getBookmarkId(context, commune);
    }
    setTimeout(() => {
      this.routingService.redirectToRoute([
        `${this.routingService.getRootByContext(context, commune.slug || commune._id)}/ansicht/${bookmarkId}`,
      ]);
    });
  }

  /**
   * Initiates the process to start the page based on the current context.
   * This method includes redirecting to a specific bookmark related to the context.
   *
   * @return {void} Does not return a value.
   */
  private callStartPageByContext(): void {
    this.redirectToBookmark();
  }

  /**
   * Retrieves the bookmark ID based on the context and the provided commune instance.
   *
   * @param {string} context - The context in which the bookmark ID is being requested,
   *                           typically a string such as 'atlasInternal'.
   * @param {Commune} commune - The commune object containing bookmark information.
   * @return {string} The bookmark ID corresponding to the specified context.
   */
  private getBookmarkId(context: string, commune: Commune): string {
    return context === 'atlasInternal'
      ? commune.defaultBookmark
      : commune.defaultBookmarkExternal;
  }

  /**
   * Loads a layout based on the given template, indicator IDs, and data layer type ID.
   *
   * @param {string[]} indicatorIds - Array of indicator IDs used to generate the grid layout.
   * @param {string} dataLayerTypeId - Identifier for the data layer type used in the grid layout.
   * @param {Partial<CustomGridStackWidget>[]} [template] - Optional partial template defining custom widget configurations.
   * @return {void} This method does not return a value.
   */
  private loadLayoutByTemplate(
    indicatorIds: string[],
    dataLayerTypeId: string,
    template?: Partial<CustomGridStackWidget>[]
  ) {
    const grid = this.templateService.generateGridFromTemplate(
      indicatorIds,
      dataLayerTypeId,
      template
    );
    setTimeout(() => {
      this.loadLayout(grid);
    }, 100);
  }

  /**
   * Adds a new view by confirming with the user. If confirmed, it clears the current state.
   *
   * @return {void} No value is returned by this method.
   */
  addNewView(): void {
    if (this.gridStore.gridHasChanges()) {
      this.confirmationService.confirmAtlasOverwrite(
        () => this.clear(),
        'Neue Ansicht zusammenstellen?'
      );
    } else {
      this.clear();
    }
  }

  /**
   * Clears the grid and resets widgets, bookmarks, and routing.
   * The method removes all items from the grid, reinitializes components,
   * adds the adder widget, and updates the bookmark and grid stores
   * to reflect the reset state. It also navigates to the root path.
   *
   * @return {void} Indicates that the method does not return any value.
   */
  private clear(): void {
    this.grid.removeAll(true);
    this.init();
    this.addAdderWidget();
    this.routingService.navigateAtlasRoot().then(() => {
      this.bookmarkStore.deselect();
      this.gridStore.unsetActiveBookmark();
      this.addAdderWidget();
    });
  }

  getActiveStep(): number {
    return typeof this.activeConfig === 'number' ? this.activeConfig - 1 : 0;
  }

  /**
   * Saves the current grid layout, updates the active bookmark with the new layout configuration,
   * and then clears the state.
   *
   * @return {void} Does not return a value.
   */
  saveAndClose() {
    this.saveGridLayout();
    const activeBookmark = this.gridStore.activeBookmark();
    const layout = this.gridStore.layout();
    if (layout && activeBookmark) {
      const newBookmark = { ...activeBookmark, ...{ config: layout } };
      this.bookmarkStore.update(newBookmark);
      this.clear();
      this.gridStore.resetGridHasChanges();
    } else {
      this.gridStore.openBookmarkForm();
    }
  }

  /**
   * Cancels the current operation and initiates the process to add a new bookmark by opening the bookmark form.
   * This method first clears any existing state or operations and then opens a form for adding or editing a bookmark.
   *
   * @return {void} This method does not return a value.
   */
  cancelAndAdd() {
    this.gridStore.openBookmarkForm();
  }
}
